import React, { useEffect, useMemo, useState } from 'react';
import Papa from 'papaparse';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import {
  set,
  map,
  last,
  keys,
  getOr,
  isEmpty,
  zipObject,
  isEqual,
  reduce,
  reject,
  toUpper
} from 'lodash/fp';
import {
  Button,
  Content,
  Uploader,
  RadioGroup,
  Radio,
  Panel,
  Grid,
  Row,
  Col,
  Icon,
  Message,
  Alert
} from 'rsuite';

import * as PERMISSIONS from '../../../constants/permissions/inventory';
import IMPORT_ITEM_COLUMNS from '../../../constants/inventory/ImportItemColumns';
import {
  SKIPPED,
  FAILED
} from '../../../constants/inventory/ImportItemResultTypes';
import HealthLevels from '../../../constants/common/HealthLevels';
import { modifyAccess } from '../../../helpers/permission';
import AccessControl from '../../../components/AccessControl';
import AccessDenied from '../../../components/AccessDenied';
import InventoryPageLayout from '../../../containers/inventory/PageLayout';
import PageHeader from '../../../components/PageHeader';
import { importInventoryItem } from '../../../redux/Inventory/sagas/item/routines';
import ImportItemsResult from '../../../components/inventory/ImportItemsResult';
import { getItems } from '../../../redux/Inventory/selectors/components/import-items-result';
import { fetchInventoryItemOptions } from '../../../redux/sagas/routines';
import { getItemOptions } from '../../../redux/Inventory/selectors/components/item-form';
import {
  clearImportItems,
  ignoredImportItem
} from '../../../redux/Inventory/actions';

const reduceWithKey = reduce.convert({ cap: false });

const StepHeading = styled.p`
  font-size: 14px;
  margin-bottom: 10px;
`;

const StepWrapper = styled.div`
  margin-bottom: 10px;
`;

const ImportMessageLabel = styled.div`
  font-size: 16px;
`;

const StoplightValues = {
  G: HealthLevels.GO,
  Y: HealthLevels.SLOW,
  R: HealthLevels.WHOA
};

const INSERT_UPDATE = 1;
const INSERT_SKIP = 2;

export default () => {
  const dispatch = useDispatch();
  const importItemsResult = useSelector(getItems);
  const itemOptions = useSelector(getItemOptions);
  const [fileList, setFileList] = useState([]);
  const [importType, setImportType] = useState(INSERT_UPDATE);
  const [itemsToImport, setItemsToImport] = useState([]);
  const [importStarted, setImportStarted] = useState(false);

  const hasItems = !isEmpty(itemsToImport);
  const importFinished =
    importStarted && itemsToImport.length === importItemsResult.length;
  const importInProgress = importStarted && !importFinished;
  const importSummaryResult = useMemo(
    () => {
      if (!importFinished) return {};

      return reduce(
        (result, { action, status }) => {
          if (status === 1) {
            const count = getOr(0, action, result) + 1;
            return set(action, count, result);
          }
          const count = getOr(0, FAILED, result) + 1;
          return set(FAILED, count, result);
        },
        {},
        importItemsResult
      );
    },
    [importFinished]
  );

  useEffect(() => {
    dispatch(fetchInventoryItemOptions());
  }, []);

  const handleStartImport = async () => {
    setImportStarted(true);
    dispatch(clearImportItems());

    const itemIds = map('value', itemOptions);
    const itemNames = map('label', itemOptions);
    const itemsMapByName = zipObject(itemNames, itemIds);

    itemsToImport.forEach((each, i) => {
      const counter = i + 1;
      const tags = reject(isEmpty, each.tags.split(','));
      const itemId = getOr(null, each.name, itemsMapByName);
      const stoplight = getOr(null, toUpper(each.stoplight), StoplightValues);
      const item = { ...each, id: itemId, stoplight, counter, tags };

      if (!itemId || importType === INSERT_UPDATE) {
        dispatch(importInventoryItem(item));
      } else {
        dispatch(ignoredImportItem({ counter, action: SKIPPED, item }));
      }
    });
  };

  const handleChangeImportType = type => {
    setImportType(type);
  };

  const handleSelectFile = files => {
    if (isEmpty(files)) return;

    const file = last(files);
    Papa.parse(file.blobFile, {
      header: true,
      skipEmptyLines: true,
      complete({ data }) {
        const dataColumns = keys(data[0]);
        if (isEqual(IMPORT_ITEM_COLUMNS, dataColumns)) setItemsToImport(data);
        else {
          setItemsToImport([]);
          Alert.error('Invalid inventory items import format.', 8000);
        }
      }
    });
    setFileList([file]);
    setImportStarted(false);
  };

  const handleRemoveFile = () => {
    setFileList([]);
    setItemsToImport([]);
  };

  const handleReset = () => {
    setFileList([]);
    setItemsToImport([]);
    setImportStarted(false);
    setImportType(INSERT_UPDATE);
    dispatch(clearImportItems());
  };

  return (
    <InventoryPageLayout>
      <AccessControl
        allowWhenAll={[modifyAccess(PERMISSIONS.ITEM)]}
        fallback={<AccessDenied />}
      >
        <PageHeader title="Import Items" />
        <Content>
          <Panel bordered>
            <Grid fluid>
              <Row>
                <Col xs={24} md={6}>
                  <StepWrapper>
                    <StepHeading>
                      <strong>STEP 1:</strong> &nbsp;Choose handling existing
                      items
                    </StepHeading>
                    <RadioGroup
                      name="import-type"
                      value={importType}
                      onChange={handleChangeImportType}
                    >
                      <Radio value={INSERT_UPDATE} disabled={importInProgress}>
                        Update existing item
                      </Radio>
                      <Radio value={INSERT_SKIP} disabled={importInProgress}>
                        Skip existing item
                      </Radio>
                    </RadioGroup>
                  </StepWrapper>
                </Col>
                <Col xs={24} md={6}>
                  <StepWrapper>
                    <StepHeading>
                      <strong>STEP 2:</strong> &nbsp;Select inventory items file
                      to upload
                    </StepHeading>
                    <Uploader
                      multiple={false}
                      accept=".csv"
                      fileList={fileList}
                      onChange={handleSelectFile}
                      onRemove={handleRemoveFile}
                      style={{ maxWidth: 400 }}
                      toggleComponentClass={props => (
                        <Button {...props}>
                          <Icon icon="upload" />
                          &nbsp; Upload inventory items
                        </Button>
                      )}
                      disabled={importInProgress}
                    />
                  </StepWrapper>
                </Col>
                <Col xs={24} md={6}>
                  <StepWrapper>
                    <StepHeading>
                      <strong>STEP 3:</strong> &nbsp;Click the button to start
                      importing items
                    </StepHeading>
                    <Button
                      appearance="primary"
                      disabled={!hasItems || importStarted}
                      onClick={handleStartImport}
                    >
                      <Icon icon="import" />
                      &nbsp; Import {itemsToImport.length || '#'} items&nbsp;
                    </Button>
                  </StepWrapper>
                </Col>
                <Col xs={24} md={4}>
                  {hasItems && (
                    <StepWrapper>
                      <StepHeading>
                        Reset settings to default
                        {!isEmpty(importItemsResult) && ' and item list'}
                      </StepHeading>

                      <Button onClick={handleReset} disabled={importInProgress}>
                        <Icon icon="refresh" />
                        &nbsp; Reset
                      </Button>
                    </StepWrapper>
                  )}
                </Col>
              </Row>
            </Grid>
          </Panel>

          <br />

          {importInProgress && (
            <Message
              showIcon
              type="info"
              title="Importing Items..."
              description={
                <ImportMessageLabel>
                  <strong>
                    {itemsToImport.length - importItemsResult.length}
                  </strong>
                  &nbsp;items left... &nbsp;
                </ImportMessageLabel>
              }
            />
          )}
          {importFinished && (
            <Message
              showIcon
              type="success"
              title="Import Finished!"
              description={
                <ImportMessageLabel>
                  <strong>{importItemsResult.length}</strong> total rows has
                  been processed.
                  {reduceWithKey(
                    (summary, count, action) => [
                      ...summary,
                      <div key={`${action}summary-`}>
                        <small>
                          {count} item(s) {action}
                        </small>
                      </div>
                    ],
                    [],
                    importSummaryResult
                  )}
                </ImportMessageLabel>
              }
            />
          )}
          <br />
          {importStarted && <ImportItemsResult items={importItemsResult} />}
        </Content>
      </AccessControl>
    </InventoryPageLayout>
  );
};
